import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import Spinner from '@/components/ui/spinner';
import { Card } from '@/components/ui/card';
import { Ora } from '../assets/svgs/Ora';
import AdminStepUpPanel from '../components/ora/Admin/AdminStepUp/AdminStepUpPanel';
import { getStoredAdminStepUpToken } from '../services/adminStepUpStorage';
import type { RootState } from '../store/store';
import { canAccessAdminDashboard } from '../constants/staffAccess';

interface AdminRouteProps {
  children: ReactNode;
}

// Gates the /admin dashboard to allowlisted admins. This runs inside
// ProtectedLayout, which already guarantees the user is authenticated and the
// profile is loaded before children render; the null-profile branch is just a
// defensive guard against a brief unhydrated state on hard refresh.
//
// Allowlisted admins then verify with a passkey (ORA-864) before the dashboard
// renders. The token lasts 24 hours; when it lapses mid-session, the axios
// client's step-up modal takes over instead of this gate. When the backend
// does not enforce step-up (or predates it), the gate steps aside.
const AdminRoute = ({ children }: AdminRouteProps): JSX.Element => {
  const { profile, status } = useSelector((state: RootState) => state.user);

  const [isGateCleared, setIsGateCleared] = useState(
    () => getStoredAdminStepUpToken() !== null
  );

  if (!profile && status !== 'failed') {
    return (
      <div
        style={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  if (!canAccessAdminDashboard(profile)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="contents" data-chat-snapshot="exclude">
      {isGateCleared ? (
        children
      ) : (
        <div className="flex min-h-full w-full items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <div className="flex flex-col gap-6 p-6 mobile:p-8">
              <div className="text-fg-primary">
                <Ora />
              </div>
              <AdminStepUpPanel
                onVerified={() => setIsGateCleared(true)}
                onStepUpNotRequired={() => setIsGateCleared(true)}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminRoute;
